import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { useIssueTemplates } from '../../hooks/useIssueTemplates';
import * as templateApi from '../../services/issueTemplateService';
import type { TemplateItem, TemplateCategory } from '../../services/issueTemplateService';
import toast from 'react-hot-toast';

interface TemplateManagementProps {
  projectId: string;
  isAdmin: boolean;
}

export default function TemplateManagement({ projectId, isAdmin }: TemplateManagementProps) {
  const { data, refetch } = useIssueTemplates(projectId);

  // 카테고리 폼 상태
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<{ categoryId: string; name: string } | null>(null);

  // 템플릿 모달 상태
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TemplateItem | null>(null);
  const [targetCategoryId, setTargetCategoryId] = useState<string | null>(null);

  const categories = data?.categories || [];

  // --- 카테고리 핸들러 ---

  const handleEditCategory = (cat: TemplateCategory) => {
    setEditingCategory({ categoryId: cat.categoryId, name: cat.name });
    setShowCategoryForm(true);
  };

  const handleDeleteCategory = async (cat: TemplateCategory) => {
    if (!confirm(`'${cat.name}' 카테고리를 삭제하시겠습니까?`)) return;
    try {
      await templateApi.deleteCategory(projectId, cat.categoryId);
      toast.success('카테고리가 삭제되었습니다.');
      refetch();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      toast.error(error.response?.data?.message || '카테고리 삭제에 실패했습니다.');
    }
  };

  // --- 템플릿 핸들러 ---

  const handleEditTemplate = (tmpl: TemplateItem, categoryId: string) => {
    setEditingTemplate(tmpl);
    setTargetCategoryId(categoryId);
    setShowTemplateModal(true);
  };

  const handleDeleteTemplate = async (tmpl: TemplateItem) => {
    if (!confirm(`'${tmpl.name}' 템플릿을 삭제하시겠습니까?`)) return;
    try {
      await templateApi.deleteTemplate(projectId, tmpl.templateId);
      toast.success('템플릿이 삭제되었습니다.');
      refetch();
    } catch {
      toast.error('템플릿 삭제에 실패했습니다.');
    }
  };

  // --- 기본 템플릿 초기화 ---

  const handleSeedDefaults = async () => {
    try {
      await templateApi.seedDefaults(projectId);
      toast.success('기본 템플릿이 생성되었습니다.');
      refetch();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      toast.error(error.response?.data?.message || '초기화에 실패했습니다.');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">작업 설명 템플릿 관리</h3>
          <p className="text-sm text-gray-500 mt-0.5">작업 생성/수정 시 사용할 설명 템플릿을 관리합니다.</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => { setEditingCategory(null); setShowCategoryForm(true); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 transition-colors"
          >
            <Plus size={14} />
            카테고리 추가
          </button>
        )}
      </div>

      {/* 카테고리가 없을 때 */}
      {categories.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-sm">등록된 템플릿이 없습니다.</p>
          {isAdmin && (
            <button
              onClick={handleSeedDefaults}
              className="mt-4 px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              기본 템플릿 초기화
            </button>
          )}
        </div>
      )}

      {/* 카테고리별 섹션 */}
      {categories.map((cat) => (
        <div key={cat.categoryId} className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
          {/* 카테고리 헤더 */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-700">{cat.name}</span>
            {isAdmin && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleEditCategory(cat)}
                  className="p-1 text-gray-400 hover:text-primary-600 rounded transition-colors"
                  title="카테고리 수정"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => handleDeleteCategory(cat)}
                  className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                  title="카테고리 삭제"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            )}
          </div>

          {/* 소속 템플릿 목록 */}
          {cat.templates.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400 italic">템플릿이 없습니다.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {cat.templates.map((tmpl) => (
                <div key={tmpl.templateId} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors">
                  <span className="text-sm text-gray-700">{tmpl.name}</span>
                  {isAdmin && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEditTemplate(tmpl, cat.categoryId)}
                        className="p-1 text-gray-400 hover:text-primary-600 rounded transition-colors"
                        title="템플릿 수정"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(tmpl)}
                        className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                        title="템플릿 삭제"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 템플릿 추가 버튼 */}
          {isAdmin && (
            <div className="px-4 py-2 border-t border-gray-100">
              <button
                onClick={() => {
                  setEditingTemplate(null);
                  setTargetCategoryId(cat.categoryId);
                  setShowTemplateModal(true);
                }}
                className="text-sm text-primary-600 hover:text-primary-700 transition-colors"
              >
                + 템플릿 추가
              </button>
            </div>
          )}
        </div>
      ))}

      {/* 카테고리 추가/수정 모달 */}
      {showCategoryForm && (
        <CategoryFormModal
          projectId={projectId}
          category={editingCategory}
          onClose={() => { setShowCategoryForm(false); setEditingCategory(null); }}
          onSave={() => { refetch(); setShowCategoryForm(false); setEditingCategory(null); }}
        />
      )}

      {/* 템플릿 추가/수정 모달 */}
      {showTemplateModal && (
        <TemplateFormModal
          projectId={projectId}
          template={editingTemplate}
          categoryId={targetCategoryId}
          categories={categories}
          onClose={() => { setShowTemplateModal(false); setEditingTemplate(null); }}
          onSave={() => { refetch(); setShowTemplateModal(false); setEditingTemplate(null); }}
        />
      )}
    </div>
  );
}

// ============================================================================
// CategoryFormModal
// ============================================================================

function CategoryFormModal({
  projectId,
  category,
  onClose,
  onSave,
}: {
  projectId: string;
  category: { categoryId: string; name: string } | null;
  onClose: () => void;
  onSave: () => void;
}) {
  const [name, setName] = useState(category?.name || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setIsSaving(true);
    try {
      if (category) {
        await templateApi.updateCategory(projectId, category.categoryId, { name: name.trim() });
        toast.success('카테고리가 수정되었습니다.');
      } else {
        await templateApi.createCategory(projectId, { name: name.trim() });
        toast.success('카테고리가 생성되었습니다.');
      }
      onSave();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      toast.error(error.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {category ? '카테고리 수정' : '카테고리 추가'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">카테고리명 *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={50}
            placeholder="예: 버그, 기능요청, 개선"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleSave(); }
            }}
          />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim() || isSaving}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {isSaving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// TemplateFormModal
// ============================================================================

function TemplateFormModal({
  projectId,
  template,
  categoryId,
  categories,
  onClose,
  onSave,
}: {
  projectId: string;
  template: TemplateItem | null;
  categoryId: string | null;
  categories: TemplateCategory[];
  onClose: () => void;
  onSave: () => void;
}) {
  const [name, setName] = useState(template?.name || '');
  const [content, setContent] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState(categoryId || categories[0]?.categoryId || '');
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingContent, setIsLoadingContent] = useState(false);

  // 수정 모드: 기존 content 로드
  useEffect(() => {
    if (template) {
      setIsLoadingContent(true);
      templateApi
        .fetchTemplate(projectId, template.templateId)
        .then((detail) => setContent(detail.content))
        .catch(() => toast.error('템플릿 내용을 불러오는데 실패했습니다.'))
        .finally(() => setIsLoadingContent(false));
    }
  }, [template, projectId]);

  const handleSave = async () => {
    if (!name.trim() || !content.trim()) return;
    setIsSaving(true);
    try {
      if (template) {
        await templateApi.updateTemplate(projectId, template.templateId, {
          categoryId: selectedCategoryId,
          name: name.trim(),
          content,
        });
        toast.success('템플릿이 수정되었습니다.');
      } else {
        await templateApi.createTemplate(projectId, {
          categoryId: selectedCategoryId,
          name: name.trim(),
          content,
        });
        toast.success('템플릿이 생성되었습니다.');
      }
      onSave();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      toast.error(error.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {template ? '템플릿 수정' : '템플릿 추가'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {isLoadingContent ? (
          <div className="py-12 text-center text-sm text-gray-500">로딩 중...</div>
        ) : (
          <div className="space-y-4">
            {/* 카테고리 선택 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">카테고리</label>
              <select
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
              >
                {categories.map((cat) => (
                  <option key={cat.categoryId} value={cat.categoryId}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* 템플릿명 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">템플릿명 *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                placeholder="예: 버그 리포트"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>

            {/* 본문 */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">본문 *</label>
                <button
                  type="button"
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {showPreview ? '편집' : '미리보기'}
                </button>
              </div>
              {showPreview ? (
                <div className="border border-gray-300 rounded-lg p-4 min-h-[200px] bg-gray-50 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {content || <span className="text-gray-400 italic">내용이 없습니다.</span>}
                </div>
              ) : (
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none min-h-[200px] resize-y"
                  placeholder="마크다운 형식으로 템플릿 내용을 작성하세요..."
                />
              )}
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim() || !content.trim() || isSaving || isLoadingContent}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {isSaving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
